<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Card } from 'primevue';
import ANButton from '@/components/common/AnButton.vue';
import SIPCalculator from '@/components/calculators/SIPCalculator.vue';
import StepUpSIPCalculator from '@/components/calculators/StepUpSIPCalculator.vue';
import SWPCalculator from '@/components/calculators/SWPCalculator.vue';
import FutureValueCalculator from '@/components/calculators/FutureValueCalculator.vue';
import RentVsBuyCalculator from '@/components/calculators/RentVsBuyCalculator.vue';

const router = useRouter();
const activeCalculator = ref(null);

// Calculator definitions
const calculators = [
  {
    id: 'sip',
    name: 'SIP Calculator',
    description: 'Calculate future value of your Systematic Investment Plan or required monthly SIP amount.',
    icon: '💰',
    component: 'SIPCalculator'
  },
  {
    id: 'stepup-sip',
    name: 'Step-up SIP Calculator', 
    description: 'Calculate returns with annual SIP amount increases to beat inflation and grow wealth faster.',
    icon: '📈',
    component: 'StepUpSIPCalculator'
  },
  {
    id: 'swp',
    name: 'SWP Calculator',
    description: 'Plan systematic withdrawals from your investments for regular income during retirement or goals.',
    icon: '💸',
    component: 'SWPCalculator'
  },
  {
    id: 'future-value',
    name: 'Future Value Calculator',
    description: 'Calculate growth of lump sum investment and regular contributions over time periods.',
    icon: '🚀',
    component: 'FutureValueCalculator'
  },
  {
    id: 'rent-vs-buy',
    name: 'Rent vs Buy Calculator',
    description: 'Compare renting versus buying property investment returns across Indian tier-1 cities.',
    icon: '🏠',
    component: 'RentVsBuyCalculator'
  }
];

// Handle calculator selection
const selectCalculator = (calculatorId) => {
  activeCalculator.value = calculatorId;
  // Scroll to calculator section
  setTimeout(() => {
    const calculatorElement = document.getElementById('calculator-section');
    if (calculatorElement) {
      calculatorElement.scrollIntoView({ behavior: 'smooth' });
    }
  }, 100);
};

// Handle back to calculators grid
const backToCalculators = () => {
  activeCalculator.value = null;
  // Scroll to top
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 100);
};

// Handle back to landing
const handleBackToLanding = () => {
  router.push('/');
};

// Set page title
onMounted(() => {
  document.title = 'Financial Calculators - AssetNivesh';
  window.scrollTo({ top: 0, behavior: 'smooth' });
});
</script>

<template>
  <div class="financial-calculators-page">
    <!-- Header -->
    <div class="page-header">
      <div class="header-container">
        <button @click="handleBackToLanding" class="back-button">
          ← Back to Home
        </button>
        <h1 class="page-title">Financial Planning Calculators</h1>
      </div>
    </div>

    <!-- Main Content -->
    <div class="page-content">
      
      <!-- Calculators Grid View -->
      <div v-if="!activeCalculator" class="calculators-grid-section">
        <div class="section-intro">
          <h2 class="section-title">Choose Your Calculator</h2>
          <p class="section-subtitle">
            Plan your financial future with our comprehensive suite of calculators designed for Indian investors.
          </p>
        </div>

        <!-- Calculators Grid -->
        <div class="calculators-grid">
          <Card 
            v-for="calculator in calculators" 
            :key="calculator.id"
            class="calculator-card"
            @click="selectCalculator(calculator.id)"
          >
            <template #content>
              <div class="calculator-content">
                <div class="calculator-icon">{{ calculator.icon }}</div>
                <h3 class="calculator-name">{{ calculator.name }}</h3>
                <p class="calculator-description">{{ calculator.description }}</p>
                <div class="calculator-action">
                  <ANButton 
                    label="Calculate Now"
                    severity="primary"
                    size="medium"
                    class="calculate-button"
                  />
                </div>
              </div>
            </template>
          </Card>
        </div>
      </div>

      <!-- Individual Calculator View -->
      <div v-else id="calculator-section" class="calculator-section">
        <div class="calculator-header">
          <button @click="backToCalculators" class="back-calc-button">
            ← Back to Calculators
          </button>
          <h2 class="calculator-title">
            {{ calculators.find(c => c.id === activeCalculator)?.name }}
          </h2>
        </div>

        <!-- Dynamic Calculator Component -->
        <div class="calculator-wrapper">
          <SIPCalculator v-if="activeCalculator === 'sip'" />
          <StepUpSIPCalculator v-else-if="activeCalculator === 'stepup-sip'" />
          <SWPCalculator v-else-if="activeCalculator === 'swp'" />
          <FutureValueCalculator v-else-if="activeCalculator === 'future-value'" />
          <RentVsBuyCalculator v-else-if="activeCalculator === 'rent-vs-buy'" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.financial-calculators-page {
  min-height: 100vh;
  background-color: var(--an-background-light);
  font-family: "Satoshi-Variable", sans-serif;
}

.page-header {
  background: var(--an-white);
  box-shadow: var(--an-card-shadow);
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid var(--an-gray3);
}

.header-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 24px;
}

.back-button {
  padding: 12px 20px;
  background: var(--an-white);
  border: 2px solid var(--an-primary);
  border-radius: 8px;
  color: var(--an-primary);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button:hover {
  background: var(--an-primary);
  color: var(--an-white);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 78, 80, 0.15);
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0;
  background: linear-gradient(135deg, var(--an-primary) 0%, var(--an-secondary) 50%, var(--an-tertiary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-content {
  padding: 12px 16px 32px;
  max-width: 1400px;
  margin: 0 auto;
}

/* Calculators Grid Section */
.calculators-grid-section {
  text-align: center;
}

.section-intro {
  margin-bottom: 32px;
}

.section-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 12px;
  background: linear-gradient(135deg, var(--an-primary) 0%, #4A90E2 50%, #7B68EE 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-subtitle {
  font-size: 1.1rem;
  color: var(--an-text-secondary);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.5;
}

.calculators-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 24px;
}

.calculator-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  height: 100%;
}

.calculator-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 78, 80, 0.15);
  border-color: var(--an-primary);
}

.calculator-content {
  padding: 24px 20px;
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.calculator-icon {
  font-size: 2.5rem;
  margin-bottom: 12px;
}

.calculator-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 10px;
}

.calculator-description {
  color: var(--an-text-secondary);
  line-height: 1.5;
  margin-bottom: 20px;
  flex-grow: 1;
  font-size: 0.95rem;
}

.calculator-action {
  margin-top: auto;
}

.calculate-button {
  width: 100%;
}

/* Individual Calculator Section */
.calculator-section {
  max-width: 1200px;
  margin: 0 auto;
}

.calculator-header {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 2px solid var(--an-gray3);
}

.back-calc-button {
  padding: 10px 16px;
  background: var(--an-background-light);
  border: 1px solid var(--an-gray3);
  border-radius: 6px;
  color: var(--an-text-primary);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.back-calc-button:hover {
  background: var(--an-gray3);
  transform: translateX(-2px);
}

.calculator-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0;
}

.calculator-wrapper {
  background: var(--an-white);
  border-radius: 12px;
  box-shadow: var(--an-card-shadow);
  padding: 32px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .calculators-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .header-container {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .page-content {
    padding: 20px 16px;
  }
  
  .calculators-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .section-title {
    font-size: 1.75rem;
  }
  
  .calculator-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .calculator-wrapper {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .calculators-grid {
    grid-template-columns: 1fr;
  }
  
  .calculator-content {
    padding: 20px 16px;
  }
}
</style>
